import type { TableProps as ReactBootstrapTableProps } from 'react-bootstrap/Table';
import { default as ReactBootstrapTable } from 'react-bootstrap/Table';

import React from 'react';
import classNames from 'classnames';

import { SORT_DIRECTION, type SortByType } from 'js/src/types';

type HeadingData = {
    text: string;
    id: string;
    disableSort?: boolean;
};

type TableHeaderProps = {
    heading: HeadingData;
    sortBy?: SortByType;
    setSort?: (sortBy: SortByType) => void;
};
const TableHeader = ({ heading, sortBy, setSort }: TableHeaderProps) => {
    const canSort = !!sortBy && !!setSort && !heading.disableSort;
    const isSortColumn = sortBy?.column === heading.id;

    const onClick = React.useCallback(() => {
        if (!canSort || !setSort) {
            return;
        }
        if (isSortColumn) {
            // If the column is already sorted, reverse the sort direction
            setSort({
                column: heading.id,
                direction:
                    sortBy?.direction === SORT_DIRECTION.ASC
                        ? SORT_DIRECTION.DESC
                        : SORT_DIRECTION.ASC,
            });
        } else {
            // If the column is not sorted, set it as the new sort column
            // and set the sort direction to ascending
            setSort({
                column: heading.id,
                direction: SORT_DIRECTION.ASC,
            });
        }
    }, [canSort, heading.id, isSortColumn, setSort, sortBy?.direction]);

    return (
        <th
            className={classNames({
                'cursor-pointer': canSort,
                'sortable-header': canSort,
            })}
            onClick={onClick}
        >
            {heading.text}
            <span
                translate="no"
                className={classNames('material-symbols-outlined ms-l5-4 sort-icon', {
                    invisible: !isSortColumn,
                })}
            >
                {isSortColumn && sortBy?.direction === SORT_DIRECTION.DESC
                    ? 'keyboard_arrow_up'
                    : 'keyboard_arrow_down'}
            </span>
        </th>
    );
};
/**
 * Even though the enum is named TableBorder,
 * but Bootstrap applies the border to table cell instead of entire table.
 */
export enum TableBorder {
    Default = 'Default', // Bootstrap by default applies border to the bottom of table cell
    Bottom = 'Bottom', // Explicitly apply border to bottom of table cell
    All = 'All', // Apply border to all sides of table cell
    None = 'None', // Don't apply any border
}

export type Props = {
    children: React.ReactNode;
    className?: string;
    border?: TableBorder;
    headings?: Array<HeadingData>;
    sortBy?: SortByType;
    setSort?: (sortBy: SortByType) => void;
    striped?: boolean;
    responsive?: boolean;
    // props for the underlying library
    reactBootstrapTableProps?: ReactBootstrapTableProps;
};

const Table = function Table({
    children,
    className,
    headings,
    sortBy,
    setSort,
    border,
    striped,
    responsive,
    reactBootstrapTableProps = {},
    ref,
}: Props & { ref?: React.Ref<HTMLDivElement> }) {
    switch (border) {
        case TableBorder.None:
            reactBootstrapTableProps.borderless = true;
            delete reactBootstrapTableProps.bordered;
            break;
        case TableBorder.All:
            reactBootstrapTableProps.bordered = true;
            delete reactBootstrapTableProps.borderless;
            break;
        case TableBorder.Bottom:
        case TableBorder.Default:
        default:
            delete reactBootstrapTableProps.bordered;
            delete reactBootstrapTableProps.borderless;
            break;
    }
    reactBootstrapTableProps.responsive = responsive;
    reactBootstrapTableProps.striped = striped;
    reactBootstrapTableProps.className = classNames('m-l5-0', reactBootstrapTableProps.className);

    return (
        <div className={classNames(className, 'lumen5-table')} ref={ref}>
            <ReactBootstrapTable {...reactBootstrapTableProps}>
                {headings && (
                    <thead>
                        <tr className="font-l5 font-l5--overline">
                            {headings.map((heading, index) => (
                                <TableHeader
                                    key={index}
                                    heading={heading}
                                    sortBy={sortBy}
                                    setSort={setSort}
                                />
                            ))}
                        </tr>
                    </thead>
                )}
                {children}
            </ReactBootstrapTable>
        </div>
    );
};

export default Table;
